import { Container, Graphics, Text, type TextStyleOptions } from 'pixi.js';
import type { Scene } from '../core/Scene';
import type { InputSnapshot } from '../input/actions';
import type { GameContext } from '../GameContext';
import { Palette } from '../render/Palette';
import { ARTIFACT_DEFS } from '../sim/artifacts/ArtifactDefs';
import {
  QUALITY_MAX, QUALITY_MIN, canUpgrade, upgradeArtifact, upgradeCost,
} from '../sim/meta/economy';
import { saveLastSeed } from '../meta/lastSeed';
import { fitScene } from '../ui/fitLayout';
import { FocusList } from '../ui/FocusList';
import { RunScene } from './RunScene';
import { logJournal } from '../meta/journal';

const COLS = 5;
const CELL_W = 172;
const CELL_H = 70;
const GAP = 10;
const ROWS = Math.ceil(ARTIFACT_DEFS.length / COLS);
const GRID_W = COLS * CELL_W + (COLS - 1) * GAP;
const DETAIL_H = 112;
const MIN_W = GRID_W + 40;
/** Layout budget: scale stays 1.0 on 1280×800 AND 1280×720-with-chrome. */
const MIN_H = 96 + ROWS * (CELL_H + GAP) + 8 + DETAIL_H + 22 + 30 + 60;

type Zone = 'grid' | 'actions' | 'descend';

interface ActionRow {
  label: string;
  enabled: boolean;
  run: () => void;
}

/**
 * THE VAULT — collection browser, bring-2 loadout picker, and shard-upgrade
 * bench. Wizard step 2 with a seed (ends in DESCEND); the menu's collection
 * browser without one (ends in DONE). Layout rebuilds on screen-size change
 * (syncLayout) — the Deck cut-off fix.
 */
export class RelicScene implements Scene {
  readonly root = new Container();
  readonly blocksUpdateBelow = true;

  private zone: Zone = 'grid';
  private gridFocus = 0;
  private readonly actionsFocus = new FocusList(0);
  private actions: ActionRow[] = [];
  private acceptDelay = 8;
  private dynamic = new Container();
  private dirty = true;
  private lastDevice: 'kb' | 'pad' = 'kb';

  private vw = 0;
  private vh = 0;
  private lastW = 0;
  private lastH = 0;
  private gridX = 0;
  private gridY = 0;
  private detailY = 0;
  private descendY = 0;

  constructor(
    private readonly ctx: GameContext,
    private readonly seed: string | null,
  ) {
    this.root.addChild(this.dynamic);
  }

  enter(): void {}
  exit(): void {}

  private syncLayout(): void {
    const { width, height } = this.ctx.app.screen;
    if (width === this.lastW && height === this.lastH) return;
    this.lastW = width;
    this.lastH = height;
    const { w, h } = fitScene(this.root, width, height, MIN_W, MIN_H);
    this.vw = w;
    this.vh = h;
    this.gridX = (w - GRID_W) / 2;
    this.gridY = 96;
    this.detailY = this.gridY + ROWS * (CELL_H + GAP) + 8;
    this.descendY = this.detailY + DETAIL_H + 22;
    this.dirty = true;
  }

  // ------------------------------------------------------------ state

  private focusedArtifactId(): string {
    return ARTIFACT_DEFS[this.gridFocus]!.id;
  }

  private buildActions(): void {
    const profile = this.ctx.profile;
    const id = this.focusedArtifactId();
    const owned = profile.ownedQuality(id);
    this.actions = [];
    if (owned === undefined) {
      this.actionsFocus.setCount(0);
      return;
    }

    const bringing = profile.data.lastLoadout.includes(id);
    this.actions.push({
      label: bringing ? 'Unbring' : (profile.data.lastLoadout.length >= 2 ? 'Bring  (swaps oldest)' : 'Bring'),
      enabled: true,
      run: () => {
        const loadout = profile.data.lastLoadout.filter((x) => x !== id);
        if (!bringing) {
          loadout.push(id);
          while (loadout.length > 2) loadout.shift();
        }
        profile.setLastLoadout(loadout);
      },
    });

    const check = canUpgrade(profile, id);
    const cost = owned < QUALITY_MAX ? upgradeCost(owned) : 0;
    this.actions.push({
      label: check === 'atCap'
        ? 'Upgrade — at 120% cap'
        : `Upgrade  +5%   ◇ ${cost}${check === 'broke' ? '  (not enough)' : ''}`,
      enabled: check === 'ok',
      run: () => {
        upgradeArtifact(profile, id);
        logJournal(`vault: upgrade ${id} → ${profile.ownedQuality(id)} (◇${profile.data.shards})`);
      },
    });
    this.actionsFocus.setCount(this.actions.length);
  }

  // ------------------------------------------------------------ update

  update(_dt: number, input: InputSnapshot): void {
    this.syncLayout();
    if (input.device !== this.lastDevice) {
      this.lastDevice = input.device;
      this.dirty = true;
    }
    if (this.acceptDelay > 0) {
      this.acceptDelay--;
      return;
    }

    if (this.zone === 'grid') {
      if (input.navX !== 0 || input.navY !== 0) {
        let col = this.gridFocus % COLS;
        let row = Math.floor(this.gridFocus / COLS);
        col = (col + input.navX + COLS) % COLS;
        row += input.navY;
        if (row >= ROWS) {
          this.zone = 'descend';
          this.dirty = true;
          return;
        }
        if (row >= 0) {
          this.gridFocus = row * COLS + col;
          this.dirty = true;
        }
      }
      if (input.confirmPressed) {
        this.buildActions();
        if (this.actions.length > 0) {
          this.zone = 'actions';
          this.actionsFocus.focus = 0;
          this.dirty = true;
        }
      }
      if (input.cancelPressed) this.ctx.scenes.pop();
      return;
    }

    if (this.zone === 'actions') {
      if (this.actionsFocus.move(input.navY)) this.dirty = true;
      if (input.confirmPressed) {
        const action = this.actions[this.actionsFocus.focus];
        if (action?.enabled) {
          action.run();
          this.buildActions(); // labels/costs may have changed
          this.dirty = true;
        }
      }
      if (input.cancelPressed) {
        this.zone = 'grid';
        this.dirty = true;
      }
      return;
    }

    // descend / done
    if (input.navY < 0) {
      this.zone = 'grid';
      this.dirty = true;
      return;
    }
    if (input.confirmPressed) {
      if (this.seed !== null) {
        saveLastSeed(this.seed);
        this.ctx.scenes.replace(new RunScene(this.ctx, this.seed));
      } else {
        this.ctx.scenes.pop();
      }
      return;
    }
    if (input.cancelPressed) this.ctx.scenes.pop();
  }

  // ------------------------------------------------------------ render

  render(_alpha: number): void {
    this.syncLayout();
    if (!this.dirty) return;
    this.dirty = false;
    this.redraw();
  }

  private redraw(): void {
    const profile = this.ctx.profile;
    const w = this.vw;
    this.dynamic.removeChildren().forEach((c) => c.destroy({ children: true }));
    const g = new Graphics();
    this.dynamic.addChild(g);
    g.rect(0, 0, w, this.vh).fill(Palette.bg);

    this.text('THE VAULT', w / 2, 24, {
      fontSize: 34, fontWeight: 'bold', fill: Palette.hudText, letterSpacing: 6,
    }, 0.5);
    this.text(
      this.seed !== null
        ? 'bring two artifacts into the descent — brought artifacts are never lost'
        : 'your banked collection — upgrades are permanent',
      w / 2, 64, { fontSize: 12, fill: Palette.hudDim }, 0.5,
    );
    this.text(`◇ ${profile.data.shards}`, this.gridX + GRID_W, 74, {
      fontSize: 16, fill: Palette.playerCool,
    }, 1);

    // Grid cells.
    ARTIFACT_DEFS.forEach((def, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const x = this.gridX + col * (CELL_W + GAP);
      const y = this.gridY + row * (CELL_H + GAP);
      const owned = profile.ownedQuality(def.id);
      const bringing = profile.data.lastLoadout.includes(def.id);
      const color = Palette.gold; // collection is base form — procs are run-only

      g.roundRect(x, y, CELL_W, CELL_H, 8)
        .fill({ color: Palette.hudPanel, alpha: 0.9 })
        .stroke({
          width: bringing ? 2.5 : 1.5,
          color: bringing ? Palette.playerCool : owned !== undefined ? color : Palette.hudDim,
          alpha: owned !== undefined ? 0.9 : 0.3,
        });

      if (owned === undefined) {
        this.text('??', x + CELL_W / 2, y + CELL_H / 2, {
          fontSize: 22, fill: Palette.hudDim,
        }, 0.5, 0.5);
        return;
      }

      g.poly([x + 18, y + 16, x + 26, y + 24, x + 18, y + 32, x + 10, y + 24]).fill(color);
      this.text(def.name, x + 34, y + 12, { fontSize: 12, fill: Palette.hudText });
      this.text(`Q ${Math.round(owned * 100)}%${bringing ? '   ◈ bringing' : ''}`, x + 34, y + 30, {
        fontSize: 11, fill: bringing ? Palette.playerCool : Palette.hudDim,
      });

      // Quality bar 0.8 → 1.2.
      const frac = (owned - QUALITY_MIN) / (QUALITY_MAX - QUALITY_MIN);
      g.roundRect(x + 12, y + CELL_H - 18, CELL_W - 24, 6, 3).fill({ color: 0x000000, alpha: 0.5 });
      g.roundRect(x + 12, y + CELL_H - 18, (CELL_W - 24) * frac, 6, 3).fill({ color, alpha: 0.9 });
    });

    // Focus ring.
    if (this.zone === 'grid' || this.zone === 'actions') {
      const col = this.gridFocus % COLS;
      const row = Math.floor(this.gridFocus / COLS);
      const x = this.gridX + col * (CELL_W + GAP);
      const y = this.gridY + row * (CELL_H + GAP);
      g.roundRect(x - 3, y - 3, CELL_W + 6, CELL_H + 6, 10)
        .stroke({ width: 2.5, color: this.zone === 'grid' ? Palette.focus : Palette.gold });
    }

    // Detail panel.
    const def = ARTIFACT_DEFS[this.gridFocus]!;
    const owned = profile.ownedQuality(def.id);
    g.roundRect(this.gridX, this.detailY, GRID_W, DETAIL_H, 8)
      .fill({ color: Palette.hudPanel, alpha: 0.7 })
      .stroke({ width: 1.5, color: Palette.hudDim, alpha: 0.4 });

    this.text(
      owned !== undefined
        ? `${def.name}   Q ${Math.round(owned * 100)}%`
        : 'Undiscovered — bank it from a run to reveal',
      this.gridX + 18, this.detailY + 12,
      { fontSize: 15, fill: owned !== undefined ? Palette.gold : Palette.hudDim },
    );

    if (owned !== undefined) {
      this.text(def.desc, this.gridX + 18, this.detailY + 36, {
        fontSize: 13, fill: Palette.hudText,
      });
      this.buildActions(); // keep labels current for display
      this.actions.forEach((action, i) => {
        const focused = this.zone === 'actions' && i === this.actionsFocus.focus;
        this.text(
          `${focused ? '▸ ' : '  '}${action.label}`,
          this.gridX + 18, this.detailY + 62 + i * 22,
          {
            fontSize: 13,
            fill: !action.enabled ? Palette.hudDim : focused ? Palette.focus : Palette.hudText,
          },
        );
      });
    }

    // Descend / Done row.
    const descendLabel = this.seed !== null ? `DESCEND — seed ${this.seed}` : 'DONE';
    this.text(
      (this.zone === 'descend' ? '▸ ' : '  ') + descendLabel,
      w / 2, this.descendY,
      {
        fontSize: 18, fontWeight: 'bold', letterSpacing: 2,
        fill: this.zone === 'descend' ? Palette.gold : Palette.hudText,
      }, 0.5,
    );

    const hint = this.lastDevice === 'pad'
      ? 'navigate  [dpad]     select  [Ⓐ]     back  [Ⓑ]'
      : 'navigate  [↑↓←→]     select  [E]     back  [Esc]';
    this.text(hint, w / 2, this.vh - 30, { fontSize: 12, fill: Palette.hudDim }, 0.5);
  }

  private text(
    content: string, x: number, y: number,
    style: TextStyleOptions,
    anchorX = 0, anchorY = 0,
  ): Text {
    const t = new Text({ text: content, style: { fontFamily: 'monospace', ...style } });
    t.anchor.set(anchorX, anchorY);
    t.position.set(x, y);
    this.dynamic.addChild(t);
    return t;
  }
}
